import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  broadcastAdminNotification,
  getNotificationStats,
  type AdminNotificationStats,
} from "../api/adminNotificationsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import AdminErrorState from "../Components/AdminErrorState";
import useAdminContentOptions from "../hooks/useAdminContentOptions";
import useAdminContentParts from "../hooks/useAdminContentParts";
import { useToast } from "../context/ToastContext";
import { useAdminSession } from "../context/AdminSessionContext";
import { hasRoleAtLeast } from "../utils/roleHelpers";
import { getAllRoles } from "../api/adminUsersApi";

type TargetMode = "all" | "roles";

const ROLE_BLACKLIST = new Set(["admin", "supermoderator", "moderator"]);

export default function AdminNotifications() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Notifications.View");
  const { allowed: canBroadcast } = usePermissionCheck("Notifications.Broadcast");
  const { session } = useAdminSession();
  const toast = useToast();

  const creatorScopeId = useMemo(() => {
    if (!session || !session.roles?.length) {
      return undefined;
    }
    const isCreator = hasRoleAtLeast(session.roles, "creator");
    const isModeratorOrAbove = hasRoleAtLeast(session.roles, "moderator");
    if (isCreator && !isModeratorOrAbove) {
      return session.id;
    }
    return undefined;
  }, [session]);

  const contentsQuery = useAdminContentOptions(allowed, creatorScopeId);
  const [targetMode, setTargetMode] = useState<TargetMode>("all");
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");
  const [actionUrl, setActionUrl] = useState("");
  const [contentId, setContentId] = useState("");
  const [contentPartId, setContentPartId] = useState("");
  const [contentSearch, setContentSearch] = useState("");

  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState<AdminNotificationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const contents = useMemo(() => contentsQuery.data ?? [], [contentsQuery.data]);
  const contentParts = useAdminContentParts(contentId || null).data ?? [];

  useEffect(() => {
    if (!allowed) {
      return;
    }

    let active = true;
    const loadRoles = async () => {
      const roles = await getAllRoles();
      if (!active) {
        return;
      }

      const cleaned = roles
        .map((role) => role.trim())
        .filter((role) => role.length > 0)
        .filter((role) => !ROLE_BLACKLIST.has(role.toLowerCase()));

      setRoleOptions(cleaned);
      if (cleaned.length > 0) {
        setSelectedRoles((prev) => (prev.length > 0 ? prev : [cleaned[0]]));
      }
    };

    void loadRoles();
    return () => {
      active = false;
    };
  }, [allowed]);

  useEffect(() => {
    if (!contentId) {
      setContentPartId("");
    }
  }, [contentId]);

  const filteredContents = useMemo(() => {
    const query = contentSearch.trim().toLowerCase();
    if (!query) {
      return contents;
    }
    return contents.filter((content) => content.title.toLowerCase().includes(query));
  }, [contentSearch, contents]);

  const loadStats = useCallback(async () => {
    if (!allowed) {
      return;
    }
    setStatsLoading(true);
    const response = await getNotificationStats(7);
    setStats(response);
    setStatsLoading(false);
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    void loadStats();
  }, [allowed, loadStats]);

  const toggleRole = (roleName: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleName) ? prev.filter((role) => role !== roleName) : [...prev, roleName],
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canBroadcast || sending) {
      return;
    }

    if (!title.trim() || !message.trim()) {
      toast.error("Başlık ve mesaj zorunludur.");
      return;
    }

    if (targetMode === "roles" && selectedRoles.length === 0) {
      toast.error("En az bir rol seçmelisiniz.");
      return;
    }

    setSending(true);
    const success = await broadcastAdminNotification({
      title: title.trim(),
      message: message.trim(),
      type,
      actionUrl: actionUrl.trim() || undefined,
      contentId: contentId.trim() || undefined,
      contentPartId: contentPartId.trim() || undefined,
      sendToAll: targetMode === "all",
      roleNames: targetMode === "roles" ? selectedRoles : undefined,
    });
    setSending(false);

    if (!success) {
      toast.error("Bildirim gönderilemedi.");
      return;
    }

    toast.success("Bildirim gönderildi.");
    setTitle("");
    setMessage("");
    setActionUrl("");
    setContentId("");
    setContentPartId("");
    void loadStats();
  };

  if (checkingPermission) {
    return <div className="workspace-panel rounded-2xl p-8 text-center text-sm text-slate-600">Kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return (
      <AdminErrorState
        status="warning"
        title="İzniniz yok"
        description="Bu sayfayı görüntülemek için gerekli yetkiye sahip değilsiniz."
        helpHref="/admin/platform?tab=help"
      />
    );
  }

  return (
    <div className="space-y-5 workspace-canvas">
      <section className="workspace-panel rounded-3xl p-6">
        <h2 className="text-xl font-bold text-slate-900">Bildirim Orkestrasyonu</h2>
        <p className="text-sm text-slate-600">
          Üye seçimi kaldırıldı. Bildirimler yalnızca "Herkese" veya "Belirli rollere" gönderilir.
        </p>
      </section>

      <section className="workspace-panel rounded-3xl p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white/80 p-3">
              <div className="text-sm font-semibold text-slate-700">Hedefleme</div>
              <div className="mt-2 space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="target-mode"
                    checked={targetMode === "all"}
                    onChange={() => setTargetMode("all")}
                  />
                  Herkese gönder
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="target-mode"
                    checked={targetMode === "roles"}
                    onChange={() => setTargetMode("roles")}
                  />
                  Belirli rollere gönder
                </label>
              </div>

              {targetMode === "roles" && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {roleOptions.length === 0 && <div className="text-xs text-slate-500">Rol listesi bulunamadı.</div>}
                  {roleOptions.map((role) => {
                    const checked = selectedRoles.includes(role);
                    return (
                      <label
                        key={role}
                        className={`flex items-center gap-2 rounded-lg border px-2 py-2 text-xs ${
                          checked ? "border-sky-400 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role)}
                        />
                        {role}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/80 p-3">
              <div className="text-sm font-semibold text-slate-700">İçerik bağlamı (opsiyonel)</div>
              <label className="mt-2 block text-xs font-semibold text-slate-600">
                İçerik
                <select
                  value={contentId}
                  onChange={(event) => setContentId(event.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">-- İçerik seç --</option>
                  {filteredContents.map((content) => (
                    <option key={content.id} value={content.id}>
                      {content.title}
                    </option>
                  ))}
                </select>
              </label>
              <input
                type="text"
                placeholder="İçerik ara..."
                value={contentSearch}
                onChange={(event) => setContentSearch(event.target.value)}
                className="mt-2 w-full rounded border border-slate-200 px-3 py-2 text-xs"
              />
              <label className="mt-2 block text-xs font-semibold text-slate-600">
                Bölüm
                <select
                  value={contentPartId}
                  onChange={(event) => setContentPartId(event.target.value)}
                  disabled={!contentId || contentParts.length === 0}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
                >
                  <option value="">{contentId ? "Tüm bölümler" : "Önce içerik seç"}</option>
                  {contentParts.map((part) => (
                    <option key={part.id} value={part.id}>
                      #{part.order} - {part.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-600">
              Başlık
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                required
              />
            </label>
            <label className="text-sm font-medium text-slate-600">
              Bildirim tipi
              <select
                value={type}
                onChange={(event) => setType(event.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="info">Bilgi</option>
                <option value="success">Başarı</option>
                <option value="warning">Uyarı</option>
                <option value="danger">Kritik</option>
              </select>
            </label>
          </div>

          <label className="text-sm font-medium text-slate-600">
            Mesaj
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={4}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              required
            />
          </label>

          <label className="text-xs font-medium text-slate-500">
            Action URL (opsiyonel)
            <input
              type="text"
              value={actionUrl}
              onChange={(event) => setActionUrl(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="/content/..."
            />
          </label>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !canBroadcast}
              className="rounded-xl bg-[linear-gradient(135deg,#0a84ff,#0a5dd7)] px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {sending ? "Gönderiliyor..." : canBroadcast ? "Bildirim Gönder" : "Gönderim izniniz yok"}
            </button>
          </div>
        </form>
      </section>

      <section className="workspace-panel rounded-2xl p-4 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-800">Gönderim İstatistikleri</h3>
            <p className="text-xs text-slate-500">Son 7 günlük bildirim özetidir.</p>
          </div>
          <button
            type="button"
            onClick={() => void loadStats()}
            className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
          >
            Yenile
          </button>
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <StatBlock label="Toplam" value={stats?.totalSent ?? 0} loading={statsLoading} />
          <StatBlock label="Okunmamış" value={stats?.unreadCount ?? 0} loading={statsLoading} />
          <StatBlock label="Başlangıç" value={formatShortDate(stats?.rangeStartUtc)} loading={statsLoading} />
          <StatBlock label="Bitiş" value={formatShortDate(stats?.rangeEndUtc)} loading={statsLoading} />
        </div>
      </section>
    </div>
  );
}

function StatBlock({ label, value, loading }: { label: string; value: string | number; loading?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/75 p-3 text-xs">
      <div className="text-[11px] uppercase text-slate-400">{label}</div>
      <div className="text-lg font-semibold text-slate-900">{loading ? "..." : value}</div>
    </div>
  );
}

function formatShortDate(value?: string) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
